import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Palmtree, Menu, X } from "lucide-react";
import { useLang } from "../i18n";

const LANGS = [
  { code: "mk", flag: "https://flagcdn.com/w40/mk.png", label: "MK" },
  { code: "en", flag: "https://flagcdn.com/w40/gb.png", label: "EN" },
  { code: "el", flag: "https://flagcdn.com/w40/gr.png", label: "ΕΛ" },
  { code: "sr", flag: "https://flagcdn.com/w40/rs.png", label: "SR" },
  { code: "ro", flag: "https://flagcdn.com/w40/ro.png", label: "RO" },
];

export const Nav = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (hash) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + hash);
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const onDark = location.pathname === "/" && !scrolled;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-sand/70 backdrop-blur-xl saturate-150 border-b border-line/60" : "bg-transparent"
      }`}
    >
      <div className="relative z-50 max-w-[1440px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className={`flex items-center gap-2 group shrink-0 mr-8 md:mr-16 ${onDark ? "text-sand" : "text-ink"}`}>
          <Palmtree className="w-5 h-5 text-ember transition-transform duration-500 group-hover:rotate-12" strokeWidth={1.5} />
          <span className="font-serif text-lg md:text-xl tracking-tight font-medium whitespace-nowrap">{t("nav.brand")}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-4 lg:gap-5 text-[12px] font-medium tracking-wide uppercase whitespace-nowrap">
          <a href="#apartments" data-testid="nav-apartments-link" onClick={goTo("#apartments")} className={`link-sweep ${onDark ? "text-sand/90" : "text-ink"}`}>
            {t("nav.apartments")}
          </a>
          <a href="#hoteli" data-testid="nav-hotels-link" onClick={goTo("#hoteli")} className={`link-sweep ${onDark ? "text-sand/90" : "text-ink"}`}>
            {t("nav.hotels")}
          </a>
          <a href="#villas" data-testid="nav-villas-link" onClick={goTo("#villas")} className={`link-sweep ${onDark ? "text-sand/90" : "text-ink"}`}>
            {t("nav.villas")}
          </a>
          <a href="#vozila" data-testid="nav-vehicles-link" onClick={goTo("#vozila")} className={`link-sweep ${onDark ? "text-sand/90" : "text-ink"}`}>
            {t("nav.vehicles")}
          </a>
          <a href="#motori" data-testid="nav-motorcycles-link" onClick={goTo("#motori")} className={`link-sweep ${onDark ? "text-sand/90" : "text-ink"}`}>
            {t("nav.motorcycles")}
          </a>
          <a href="#velosipedi" data-testid="nav-bicycles-link" onClick={goTo("#velosipedi")} className={`link-sweep ${onDark ? "text-sand/90" : "text-ink"}`}>
            {t("nav.bicycles")}
          </a>
          <a href="#ketering" data-testid="nav-catering-link" onClick={goTo("#ketering")} className={`link-sweep ${onDark ? "text-sand/90" : "text-ink"}`}>
            {t("nav.catering")}
          </a>
          <a href="#turi" data-testid="nav-tours-link" onClick={goTo("#turi")} className={`link-sweep ${onDark ? "text-sand/90" : "text-ink"}`}>
            {t("nav.tours")}
          </a>
          <a href="#gevgelija" data-testid="nav-about-link" onClick={goTo("#gevgelija")} className={`link-sweep ${onDark ? "text-sand/90" : "text-ink"}`}>
            {t("nav.about")}
          </a>
          <a href="#contact" data-testid="nav-contact-link" onClick={goTo("#contact")} className={`link-sweep ${onDark ? "text-sand/90" : "text-ink"}`}>
            {t("nav.contact")}
          </a>
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <div className={`flex items-center border ${onDark ? "border-sand/30" : "border-ink/20"}`} data-testid="lang-toggle">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-toggle-${l.code}`}
                onClick={() => setLang(l.code)}
                title={l.label}
                className={`px-2 py-1.5 flex items-center gap-1.5 transition-colors duration-300 ${lang === l.code ? "bg-terra" : onDark ? "hover:bg-sand/10" : "hover:bg-ink/5"}`}
              >
                <img src={l.flag} alt={l.label} className="w-5 h-[14px] object-cover" />
              </button>
            ))}
          </div>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
            className={`md:hidden w-10 h-10 flex items-center justify-center border ${onDark ? "border-sand/30 text-sand" : "border-ink/20 text-ink"}`}
          >
            {menuOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
          </button>
          <a
            href="#contact"
            data-testid="nav-book-cta"
            onClick={goTo("#contact")}
            className="hidden sm:inline-flex items-center px-5 py-2.5 text-xs font-semibold tracking-widest uppercase whitespace-nowrap bg-terra text-sand hover:bg-clay transition-colors duration-300"
          >
            {t("nav.book")}
          </a>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="mobile-menu-overlay"
            className="fixed inset-0 z-40 bg-coal flex flex-col items-center justify-start pt-24 pb-10 gap-6 overflow-y-auto md:hidden"
          >
            <a href="#apartments" data-testid="mobile-nav-apartments" onClick={goTo("#apartments")} className="font-serif text-4xl text-sand tracking-tight hover:text-ember transition-colors">
              {t("nav.apartments")}
            </a>
            <a href="#hoteli" data-testid="mobile-nav-hotels" onClick={goTo("#hoteli")} className="font-serif text-4xl text-sand tracking-tight hover:text-ember transition-colors">
              {t("nav.hotels")}
            </a>
            <a href="#villas" data-testid="mobile-nav-villas" onClick={goTo("#villas")} className="font-serif text-4xl text-sand tracking-tight hover:text-ember transition-colors">
              {t("nav.villas")}
            </a>
            <a href="#vozila" data-testid="mobile-nav-vehicles" onClick={goTo("#vozila")} className="font-serif text-2xl text-sand/80 tracking-tight hover:text-ember transition-colors">
              {t("nav.vehicles")}
            </a>
            <a href="#motori" data-testid="mobile-nav-motorcycles" onClick={goTo("#motori")} className="font-serif text-2xl text-sand/80 tracking-tight hover:text-ember transition-colors">
              {t("nav.motorcycles")}
            </a>
            <a href="#velosipedi" data-testid="mobile-nav-bicycles" onClick={goTo("#velosipedi")} className="font-serif text-2xl text-sand/80 tracking-tight hover:text-ember transition-colors">
              {t("nav.bicycles")}
            </a>
            <a href="#ketering" data-testid="mobile-nav-catering" onClick={goTo("#ketering")} className="font-serif text-2xl text-sand/80 tracking-tight hover:text-ember transition-colors">
              {t("nav.catering")}
            </a>
            <a href="#turi" data-testid="mobile-nav-tours" onClick={goTo("#turi")} className="font-serif text-2xl text-sand/80 tracking-tight hover:text-ember transition-colors">
              {t("nav.tours")}
            </a>
            <a href="#gevgelija" data-testid="mobile-nav-about" onClick={goTo("#gevgelija")} className="font-serif text-4xl text-sand tracking-tight hover:text-ember transition-colors">
              {t("nav.about")}
            </a>
            <a href="#contact" data-testid="mobile-nav-contact" onClick={goTo("#contact")} className="font-serif text-4xl text-sand tracking-tight hover:text-ember transition-colors">
              {t("nav.contact")}
            </a>
            <a
              href="#contact"
              data-testid="mobile-nav-book"
              onClick={goTo("#contact")}
              className="mt-4 inline-flex items-center px-8 py-4 bg-terra text-sand text-xs font-bold tracking-[0.2em] uppercase"
            >
              {t("nav.book")}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
